import EntityController from './EntityController';
import getService from '../services/serviceDiscovery';
import { SERVICE_TYPE, PERMISSION_TYPE, LEGO_CONSTANTS, ENTITY_FIELD_TYPE } from 'constants/index';
import {
  CommonUtils,
  getLookupChoicesForField,
  isAutoCompleteLookupSourceField,
  findDependentChoiceIdPathByValue,
  findDependentChoiceIdPathById,
  findDependentChoiceValuePathById,
} from 'utils';
import { serializeDateTimeToISO, splitDateTimeFormat } from 'utils/dateUtils';
class FormRendererController extends EntityController {
  parseSchemaFields = (entity, values, nativeObjectData, choicesByFieldId = new Map(), timezone) => {
    const entityFieldLookup = this.getFieldLookup(entity);
    const nativeIds = Object.keys(nativeObjectData.idLookup);
    return Object.keys(values).reduce((acc, key) => {
      const field = entityFieldLookup[key];
      // Nested-select choice trees stamp per-level names (e.g. category, sub_category) from native
      // levels metadata. Those keys can end up in fw-form values but are not entity fields — skip
      // them so we neither crash on field.type nor send junk in the record payload.
      if (!field) {
        return acc;
      }
      if (field.type === ENTITY_FIELD_TYPE.CHECKBOX) {
        let toSave = values[key];
        if (values[key] === null) {
          toSave = false;
        }
        return { ...acc, [key]: toSave };
      }
      if (values[key] !== undefined && values[key] !== '') {
        if (field.type === ENTITY_FIELD_TYPE.DROPDOWN_LOOKUP_SECTION) {
          // The API expects each selected cascade level saved under its own dependent sub-field -
          // <field.name>_dd<level>: id - rather than a single value under the parent field's own name
          // (mirrors the `fields` array declared for this type at save time, see buildDependentLookupFields).
          // Only levels actually reached by the selected path are sent - deeper levels are simply omitted
          // (no option was available there) rather than sent as null; the display side resolves the value
          // from whichever level is deepest (see normalizeDependentLookupRecordData), so it doesn't matter
          // whether an unreached level is omitted or explicitly null.
          // fw-co-nested-select tracks selection by display value (see getFormSchema), so resolve that
          // value back to the id at every level of the selected path here, right before the record is saved.
          const choices = getLookupChoicesForField(field, choicesByFieldId) ?? [];
          // An edit-mode field the user never touched still holds its seeded initial value - the saved
          // record's raw leaf id, not a display value (seeding never fires fw-co-nested-select's fwChange,
          // see findDependentChoiceIdPathById) - so fall back to resolving values[key] as an id when it
          // doesn't match any display value, instead of dropping straight to the raw-value fallback below.
          const idPath = findDependentChoiceIdPathByValue(choices, values[key]) ?? findDependentChoiceIdPathById(choices, values[key]);
          if (idPath) {
            // FORMSERV requirement: AUTO_COMPLETE-backed sources (see isAutoCompleteLookupSourceField) must
            // have every level's id saved as a string - getFormSchema's DROPDOWN_LOOKUP branch mirrors this
            // so edit-mode reseeding matches on the same type; DROPDOWN_LOOKUP_SECTION's own edit-mode path
            // already normalizes via String() in findDependentChoiceValuePathById, so no matching change is
            // needed there.
            const saveIdAsString = isAutoCompleteLookupSourceField(field);
            return idPath.reduce((levelAcc, id, index) => ({ ...levelAcc, [`${key}_dd${index + 1}`]: saveIdAsString ? String(id) : id }), acc);
          }
          // Couldn't resolve any id (choices not loaded yet, or a stale/unknown value) - fall back to the
          // raw display value under the field's own name so the selection isn't silently dropped.
          return { ...acc, [key]: values[key] };
        }

        let toSave = values[key];
        if (values[key] && nativeIds.includes(field?.related_entity_id?.toString())) {
          toSave = BigInt(values[key]);
        }

        if (field.type === ENTITY_FIELD_TYPE.DROPDOWN) {
          toSave = entity?.translatedEntityReverseChoicesLookup?.get(key)?.[key]?.[values[key]] ?? values[key];
        }

        if (field.type === ENTITY_FIELD_TYPE.DROPDOWN_LOOKUP && isAutoCompleteLookupSourceField(field)) {
          // FORMSERV requirement: AUTO_COMPLETE-backed sources must save the choice id as a string.
          // getFormSchema's DROPDOWN_LOOKUP branch mirrors this by also stringifying the choice id used as
          // each option's value, so the saved value here and what the select is seeded with on edit match.
          toSave = String(values[key]);
        }

        if (field.type === ENTITY_FIELD_TYPE.MULTI_SELECT) {
          toSave = values[key]?.map(itemValue => {
            return entity?.translatedEntityReverseChoicesLookup?.get(key)?.[key]?.[itemValue] ?? itemValue;
          });
        }

        if (field.type === ENTITY_FIELD_TYPE.DATE_TIME) {
          toSave = values[key] ? serializeDateTimeToISO(values[key], timezone) : values[key];
        }

        return { ...acc, [key]: toSave };
      }
      return { ...acc, [key]: null };
    }, {});
  };

  __updateRecord = async (ids, payload) => {
    try {
      if (!this.hasRecordPermission(PERMISSION_TYPE.UPDATE)) throw this.constructErrorObject(LEGO_CONSTANTS.NO_RECORD_UPDATE_PERMISSION);
      const service = getService(SERVICE_TYPE.ENTITY_RECORD);
      const record = await service.put(ids, payload);
      return this.buildResponse(true, record);
    } catch (error) {
      console.error(error);
      return this.buildErrorResponse(error.data, false);
    }
  };

  __createRecord = async (ids, payload) => {
    try {
      if (!this.hasRecordPermission(PERMISSION_TYPE.CREATE)) throw this.constructErrorObject(LEGO_CONSTANTS.NO_RECORD_CREATE_PERMISSION);
      const service = getService(SERVICE_TYPE.ENTITY_RECORD);
      const record = await service.create({ id: ids.id }, payload);
      return this.buildResponse(true, record);
    } catch (error) {
      console.error(error);
      return this.buildErrorResponse(error.data, false);
    }
  };

  __saveNativeRecordAssociation = async (ids, payload) => {
    if (!this.hasRecordPermission(PERMISSION_TYPE.CREATE)) throw this.constructErrorObject(LEGO_CONSTANTS.NO_RECORD_CREATE_PERMISSION);
    const service = getService(SERVICE_TYPE.ENTITY_RECORD_ASSOCATION);
    return await service.create(ids, payload).catch(e => console.error(e));
  };

  saveRecord = async (ids, entity, nativeObjectData, isEdit, values, version, choicesByFieldId, timezone) => {
    const fields = this.parseSchemaFields(entity, values, nativeObjectData, choicesByFieldId, timezone);
    const payload = {
      data: fields,
      version: isEdit ? version : 1,
    };
    const response = isEdit ? await this.__updateRecord(ids, payload) : await this.__createRecord(ids, payload);
    return response;
  };

  getFieldErrors = error => {
    if (error.error_type === LEGO_CONSTANTS.INVALID_INPUT) {
      const fieldErrors = {};
      error.errors.forEach(err => {
        fieldErrors[err.name] = err.message;
      });
      return fieldErrors;
    } else this.getErrorMessage(error);
  };

  getFormSchema = (entity, searchRecordsCallback, t, choicesByFieldId = new Map(), initialValues = {}, userLocale) => {
    const { datePart, timePart } = splitDateTimeFormat(userLocale?.dateTimeFormat);
    const fields = entity?.fields?.map(field => {
      field.placeholder = CommonUtils.getPlaceholdersForFieldTypes(field.type, t);
      if (field.type === ENTITY_FIELD_TYPE.DROPDOWN || field.type === ENTITY_FIELD_TYPE.MULTI_SELECT) {
        const choices = field?.choices?.map(ch => {
          return { ...ch, text: ch.value, value: ch.value };
        });
        return {
          ...field,
          choices,
        };
      } else if (field.type === ENTITY_FIELD_TYPE.DROPDOWN_LOOKUP) {
        // fw-form-control renders DROPDOWN_LOOKUP as a plain select; choicesByFieldId resolves the actual
        // selectable values fetched via native-dropdown-choices. The saved record should reference the
        // choice's id, so text (display) and value (stored) are sourced from different keys.
        // FORMSERV requirement: AUTO_COMPLETE-backed sources save that id as a string (see
        // parseSchemaFields) - the option value here is stringified to match, so fw-form seeds the select
        // with the previously-saved choice selected on edit instead of showing blank because of a
        // string/number type mismatch.
        const saveIdAsString = isAutoCompleteLookupSourceField(field);
        const choices = (getLookupChoicesForField(field, choicesByFieldId) ?? []).map(ch => {
          return { ...ch, text: ch.value, value: saveIdAsString ? String(ch.id) : ch.id };
        });
        return {
          ...field,
          choices,
        };
      } else if (field.type === ENTITY_FIELD_TYPE.DROPDOWN_LOOKUP_SECTION) {
        // fw-form-control renders DROPDOWN_LOOKUP_SECTION via fw-co-nested-select, which expects a nested
        // choice tree ({value, text, choices: [...]}) rather than the flat list DROPDOWN_LOOKUP takes above -
        // so the resolved choices are passed through unmodified. option_value_path is overridden to 'value'
        // (rather than fw-co-nested-select's own 'id' default) - selection tracking across cascading levels
        // only works reliably when matched against the display value; parseSchemaFields resolves the
        // matching choice's id from this value right before the record is saved.
        const choices = getLookupChoicesForField(field, choicesByFieldId) ?? [];
        // Only the leaf id is persisted on the record, so fw-co-nested-select needs the display value at
        // every level of the saved selection (not just the leaf's) to restore the full cascade on edit.
        const valuePath = findDependentChoiceValuePathById(choices, initialValues?.[field.name]);
        return {
          ...field,
          // The backend sends editable: false for this field (it has a nested `fields` array describing
          // its cascading levels, and that flag means the field's *definition* is locked - same convention
          // as the generic SECTION type). fw-form's isDisabledField() only knows one meaning for `editable`
          // though, and disables the whole fw-co-nested-select control if left as-is. Unlike SECTION (a
          // pure grouping type with no control of its own), DROPDOWN_LOOKUP_SECTION *is* a real value the
          // user must pick, so force it editable here for record entry regardless of the schema-lock flag.
          editable: true,
          field_options: { ...field.field_options, option_value_path: 'value' },
          choices,
          valuePath,
        };
      } else if (field.type === ENTITY_FIELD_TYPE.TEXT) {
        return {
          ...field,
          maxlength: LEGO_CONSTANTS.MAX_TEXT_LENGTH,
        };
      } else if (field.type === ENTITY_FIELD_TYPE.PARAGRAPH) {
        return {
          ...field,
          resize: 'vertical',
        };
      } else if (field.type === ENTITY_FIELD_TYPE.RELATIONSHIP) {
        return {
          ...field,
          search: async term => {
            return searchRecordsCallback(term, field.related_entity_id, field.field_options);
          },
        };
      } else if (field.type === ENTITY_FIELD_TYPE.DATE) {
        return {
          ...field,
          displayFormat: datePart ?? LEGO_CONSTANTS.DEFAULT_DATE_FORMAT,
          maxYear: new Date().getFullYear() + 50,
          minYear: new Date().getFullYear() - 100,
          clearInput: true,
          showFooter: false,
          readonly: true,
          fullWidth: true,
        };
      } else if (field.type === ENTITY_FIELD_TYPE.DATE_TIME) {
        return {
          ...field,
          displayFormat: datePart ?? LEGO_CONSTANTS.DEFAULT_DATE_FORMAT,
          timeFormat: timePart ?? LEGO_CONSTANTS.DEFAULT_TIME_FORMAT,
          showTimePicker: true,
          maxYear: new Date().getFullYear() + 50,
          minYear: new Date().getFullYear() - 100,
          clearInput: true,
          // Unlike DATE, this needs the footer's Cancel/Update buttons (crayons default, showFooter: true) -
          // with showTimePicker on, a single date click auto-commits before a time is ever picked, emitting
          // an invalid/undefined value and closing the popover prematurely.
          readonly: true,
          fullWidth: true,
          placeholder: LEGO_CONSTANTS.DEFAULT_DATE_TIME_PLACEHOLDER,
          interval: 1,
          timeProps: {
            minTime: '00:00',
            maxTime: '23:59',
            interval: 1,
          },
          serialize: serializeDateTimeToISO,
        };
      } else if (field.type === ENTITY_FIELD_TYPE.PRIMARY) {
        return {
          ...field,
          type: ENTITY_FIELD_TYPE.TEXT,
          maxlength: LEGO_CONSTANTS.MAX_TEXT_LENGTH,
        };
      } else return field;
    });

    return {
      ...entity,
      fields: fields,
    };
  };
}

export const formRendererController = new FormRendererController();
